import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { ChevronDown, ChevronRight, Loader2, Plus, Trash2 } from 'lucide-react'
import {
  createPlan, deactivatePlan, deletePlanPrice, listPlans, majorToMinor,
  minorToMajor, updatePlan, upsertPlanPrice,
  type AdminPlan, type PlanFeature, type PlanResourceLimit,
} from '@/api/billingAdmin'
import {
  getResourceLimits, type ResourceLimitCatalog, type ResourceLimitPolicy,
} from '@/api/instanceAdmin'
import { COUNTRIES } from '@/data/countries'
import {
  AdminModal, Field, InfoBox, LimitInput, Td, Th, chipStyle, errText, tableStyle,
} from '../adminUi'

/** The built-in plan codes: editable, but never deletable or renamable. */
const BUILT_IN = new Set(['F', 'P', 'S'])
const CURRENCIES = ['USD', 'INR', 'EUR', 'GBP', 'AED', 'SGD']

function limitChip(value: number | null): string {
  if (value === null || value === undefined) return 'Unlimited'
  return value.toLocaleString()
}

export default function PlansPanel() {
  const [plans, setPlans] = useState<AdminPlan[]>([])
  const [features, setFeatures] = useState<PlanFeature[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<AdminPlan | null>(null)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    try {
      const data = await listPlans()
      setPlans(data.plans)
      setFeatures(data.features)
    } catch (e) {
      toast.error(errText(e, 'Could not load the plan catalog'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const toggleActive = async (plan: AdminPlan) => {
    if (plan.id === null) return
    try {
      await updatePlan(plan.id, { is_active: !plan.is_active })
      await load()
    } catch (e) {
      toast.error(errText(e, 'Could not update the plan'))
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spin" /></div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <InfoBox>
        Plans are what users can be on. Their codes are identity — every existing
        user, subscription and history row points at one — so <strong>F</strong>,{' '}
        <strong>P</strong> and <strong>S</strong> can be edited but not renamed or
        removed. A blank limit means <strong>unlimited</strong>; <strong>0</strong>{' '}
        means none allowed at all.
      </InfoBox>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          <Plus size={14} /> New plan
        </button>
      </div>

      <table style={tableStyle}>
        <thead>
          <tr>
            <Th>Code</Th>
            <Th>Name</Th>
            <Th right>Monthly</Th>
            <Th right>Yearly</Th>
            <Th right>Credits/mo</Th>
            <Th right>Apps</Th>
            <Th right>Tables</Th>
            <Th right>Records</Th>
            <Th right>Turns/day (credits · own)</Th>
            <Th>Features</Th>
            <Th right>Active</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {plans.map((plan) => {
            const monthly = plan.prices.find((p) => p.billing_period === 'monthly' && !p.country)
            const yearly = plan.prices.find((p) => p.billing_period === 'yearly' && !p.country)
            return (
              <tr key={plan.code}>
                <Td><span style={chipStyle}>{plan.code}</span></Td>
                <Td>{plan.display_name}</Td>
                <Td right>{monthly ? `${monthly.currency} ${minorToMajor(monthly.amount_minor)}` : '—'}</Td>
                <Td right>{yearly ? `${yearly.currency} ${minorToMajor(yearly.amount_minor)}` : '—'}</Td>
                <Td right>{plan.included_monthly_credits.toLocaleString()}</Td>
                <Td right>{limitChip(plan.max_applications)}</Td>
                <Td right>{limitChip(plan.max_tables_per_project)}</Td>
                <Td right>{limitChip(plan.max_records_per_project)}</Td>
                <Td right>{limitChip(plan.max_credit_turns_per_day)} · {limitChip(plan.max_own_ai_turns_per_day)}</Td>
                <Td>
                  {plan.allow_runtime_download
                    ? <span style={chipStyle}>Offline download</span>
                    : <span style={{ color: 'var(--color-text-secondary)' }}>—</span>}
                </Td>
                <Td right>
                  <input
                    type="checkbox"
                    checked={plan.is_active}
                    onChange={() => toggleActive(plan)}
                    disabled={plan.id === null}
                  />
                </Td>
                <Td right>
                  <button className="btn btn-secondary" style={{ padding: '0.15rem 0.5rem' }}
                          onClick={() => setEditing(plan)}>
                    Edit
                  </button>
                </Td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {(editing || creating) && (
        <PlanEditor
          plan={editing}
          features={features}
          onClose={() => { setEditing(null); setCreating(false) }}
          onSaved={async () => { setEditing(null); setCreating(false); await load() }}
        />
      )}
    </div>
  )
}

function PlanEditor({ plan, features, onClose, onSaved }: {
  plan: AdminPlan | null
  features: PlanFeature[]
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const isNew = plan === null
  const [form, setForm] = useState<Partial<AdminPlan>>(plan ?? {
    code: '', display_name: '', included_monthly_credits: 0, trial_credits: 0,
    max_applications: null, max_users_per_tenant: null,
    max_tables_per_project: null, max_screens_per_project: null,
    max_records_per_project: null, max_own_ai_turns_per_day: null,
    max_credit_turns_per_day: null,
    allow_runtime_download: false,
    download_fee_usd_minor: 0, download_fee_inr_minor: 0, grace_days: 7, is_active: true,
    is_public: true, sort_order: 50,
  })
  const [prices, setPrices] = useState(plan?.prices ?? [])
  const [priceDraft, setPriceDraft] = useState<PriceDraft>(EMPTY_PRICE_DRAFT)
  const [saving, setSaving] = useState(false)

  const set = <K extends keyof AdminPlan>(key: K, value: AdminPlan[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const save = async () => {
    if (!form.code || !form.display_name) {
      toast.error('A code and a display name are required')
      return
    }
    setSaving(true)
    try {
      const body = { ...form, resource_limits: cleanResourceLimits(form.resource_limits) }
      if (isNew) await createPlan(body)
      else if (plan?.id !== null && plan?.id !== undefined) {
        // A price typed but not yet added would otherwise be dropped silently.
        if (!(await addPrice(true))) return
        await updatePlan(plan.id, body)
      }
      toast.success('Plan saved')
      await onSaved()
    } catch (e) {
      toast.error(errText(e, 'Could not save the plan'))
    } finally {
      setSaving(false)
    }
  }

  /** Save the typed price row; true when saved (or nothing was typed). */
  const addPrice = async (quiet = false): Promise<boolean> => {
    const row = draftToPrice(priceDraft)
    if (!row) {
      if (!quiet) toast.error('Enter an amount')
      return quiet
    }
    if (plan?.id === null || plan?.id === undefined) {
      toast.error('Save the plan first, then add its prices')
      return false
    }
    try {
      setPrices(await upsertPlanPrice(plan.id, { ...row, provider: 'razorpay' }))
      setPriceDraft((d) => ({ ...d, amount: '', providerPlanId: '' }))
      if (!quiet) toast.success('Price saved')
      return true
    } catch (e) {
      toast.error(errText(e, 'Could not save the price'))
      return false
    }
  }

  const removePrice = async (priceId: number) => {
    if (plan?.id === null || plan?.id === undefined) return
    try {
      await deletePlanPrice(plan.id, priceId)
      setPrices((p) => p.filter((r) => r.id !== priceId))
    } catch (e) {
      toast.error(errText(e, 'Could not remove the price'))
    }
  }

  return (
    <AdminModal title={isNew ? 'New plan' : `Edit ${plan?.display_name}`} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 2fr' }}>
          <Field
            label="Code"
            hint={!isNew && BUILT_IN.has(form.code || '')
              ? 'Built-in — every user and history row points at this code.'
              : 'Short identifier, e.g. TEAM. Cannot change later.'}
          >
            <input
              value={form.code || ''}
              onChange={(e) => set('code', e.target.value.toUpperCase())}
              disabled={!isNew}
              style={{ width: '100%' }}
            />
          </Field>
          <Field label="Display name" hint="What users see everywhere — plan cards, the user menu, invoices.">
            <input
              value={form.display_name || ''}
              onChange={(e) => set('display_name', e.target.value)}
              style={{ width: '100%' }}
            />
          </Field>
        </div>

        <Field label="Description">
          <textarea
            value={form.description || ''}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
            style={{ width: '100%' }}
          />
        </Field>

        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr 1fr' }}>
          <Field label="Credits per month" hint="Granted on activation and each renewal; expire at period end.">
            <input type="number" min={0} value={form.included_monthly_credits ?? 0}
                   onChange={(e) => set('included_monthly_credits', parseFloat(e.target.value) || 0)}
                   style={{ width: '100%' }} />
          </Field>
          <Field label="Trial credits" hint="One-time welcome grant on this plan. Never repeats.">
            <input type="number" min={0} value={form.trial_credits ?? 0}
                   onChange={(e) => set('trial_credits', parseFloat(e.target.value) || 0)}
                   style={{ width: '100%' }} />
          </Field>
          <Field label="Grace days" hint="How long after expiry before the lapse policy applies.">
            <input type="number" min={0} value={form.grace_days ?? 7}
                   onChange={(e) => set('grace_days', parseInt(e.target.value, 10) || 0)}
                   style={{ width: '100%' }} />
          </Field>
        </div>

        <div>
          <div style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.5rem' }}>Limits</div>
          <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <Field label="Applications">
              <LimitInput value={form.max_applications ?? null}
                          onChange={(v) => set('max_applications', v)} noun="applications" />
            </Field>
            <Field label="Users of this plan per organization">
              <LimitInput value={form.max_users_per_tenant ?? null}
                          onChange={(v) => set('max_users_per_tenant', v)} noun="users" />
            </Field>
            <Field label="Tables per application">
              <LimitInput value={form.max_tables_per_project ?? null}
                          onChange={(v) => set('max_tables_per_project', v)} noun="tables" />
            </Field>
            <Field label="Screens per application">
              <LimitInput value={form.max_screens_per_project ?? null}
                          onChange={(v) => set('max_screens_per_project', v)} noun="screens" />
            </Field>
            <Field label="Records per application">
              <LimitInput value={form.max_records_per_project ?? null}
                          onChange={(v) => set('max_records_per_project', v)} noun="records" />
            </Field>
            <Field label="Agent turns per day on AI credits">
              <LimitInput value={form.max_credit_turns_per_day ?? null}
                          onChange={(v) => set('max_credit_turns_per_day', v)} noun="turns" />
            </Field>
            <Field label="Agent turns per day on own key / free model">
              <LimitInput value={form.max_own_ai_turns_per_day ?? null}
                          onChange={(v) => set('max_own_ai_turns_per_day', v)} noun="turns" />
            </Field>
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
            Size limits block growth only: an application at a limit keeps running and
            can still be edited, but nothing new of that kind can be added. Tables and
            screens are counted the moment one would be created; records use a count
            refreshed every few minutes, so an app can briefly overshoot that one.
            Agent turns are counted as they start (an Ask-AI prompt, or a Build start /
            continue / resume / change request), per user per UTC day, separately for
            turns on AI credits and turns on the user's own key or a free model. Both
            run on this server; only credit turns earn margin, so give them the larger
            allowance.
          </div>
        </div>

        <ResourceLimitsEditor
          value={form.resource_limits || {}}
          onChange={(v) => set('resource_limits', v)}
        />

        <div>
          <div style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.5rem' }}>Features</div>
          {features.map((f) => (
            <label key={f.key} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={!!form[f.key as keyof AdminPlan]}
                onChange={(e) => set(f.key as keyof AdminPlan, e.target.checked as never)}
              />
              <span>
                {f.label}
                <span style={{ display: 'block', color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
                  {f.help}
                </span>
              </span>
            </label>
          ))}
          <div style={{ marginTop: '0.5rem', display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr', maxWidth: 460 }}>
            <Field label="Download fee — USD"
                   hint="0 = free. Charged in money to the APPLICATION's wallet, never in credits.">
              <input type="number" min={0} step="0.01"
                     value={(form.download_fee_usd_minor ?? 0) / 100}
                     onChange={(e) => set('download_fee_usd_minor', Math.round((parseFloat(e.target.value) || 0) * 100) as never)}
                     style={{ width: '100%' }} />
            </Field>
            <Field label="Download fee — INR" hint="For owners billing in INR.">
              <input type="number" min={0} step="0.01"
                     value={(form.download_fee_inr_minor ?? 0) / 100}
                     onChange={(e) => set('download_fee_inr_minor', Math.round((parseFloat(e.target.value) || 0) * 100) as never)}
                     style={{ width: '100%' }} />
            </Field>
          </div>
        </div>

        {isNew && (
          <InfoBox>Save the plan first, then open it again to add its monthly and yearly prices.</InfoBox>
        )}
        {!isNew && (
          <PriceRepeater
            prices={prices}
            draft={priceDraft}
            onDraft={setPriceDraft}
            onAdd={() => { void addPrice() }}
            onRemove={removePrice}
          />
        )}

        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.875rem' }}>
          <label style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
            <input type="checkbox" checked={!!form.is_active}
                   onChange={(e) => set('is_active', e.target.checked)} />
            Active
          </label>
          <label style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
            <input type="checkbox" checked={!!form.is_public}
                   onChange={(e) => set('is_public', e.target.checked)} />
            Show on the plans page
          </label>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <Loader2 size={14} className="spin" /> : 'Save plan'}
          </button>
        </div>
      </div>
    </AdminModal>
  )
}

/* ── Resource limits on a plan ─────────────────────────────────────── */

type PlanResourceLimits = Record<string, PlanResourceLimit>

/** Drop empty fields and keys, so an untouched limit means "catalog default". */
function cleanResourceLimits(v: PlanResourceLimits | undefined): PlanResourceLimits {
  const out: PlanResourceLimits = {}
  for (const [k, entry] of Object.entries(v || {})) {
    const e: PlanResourceLimit = {}
    for (const f of ['base', 'per_user', 'max'] as const) {
      const n = entry?.[f]
      if (n !== null && n !== undefined && Number.isFinite(n)) e[f] = n
    }
    if (Object.keys(e).length) out[k] = e
  }
  return out
}

/** The effective value for `users` — the same rule the server applies. */
function effectiveLimit(
  entry: PlanResourceLimit | undefined, def: ResourceLimitCatalog['limits'][string],
  ceiling: number, users: number,
): number {
  let v = (entry?.base ?? def.default) + (def.scales ? (entry?.per_user ?? 0) * users : 0)
  if (entry?.max !== null && entry?.max !== undefined) v = Math.min(v, entry.max)
  return Math.min(Math.max(v, def.min), ceiling)
}

/**
 * What one application on this plan may use of the shared server. Each value
 * is base + per user × users, capped at the plan's max and then at the
 * instance ceiling. Blank = the catalog default.
 */
function ResourceLimitsEditor({ value, onChange }: {
  value: PlanResourceLimits
  onChange: (v: PlanResourceLimits) => void
}) {
  const [open, setOpen] = useState(Object.keys(value).length > 0)
  const [catalog, setCatalog] = useState<ResourceLimitCatalog | null>(null)
  const [policy, setPolicy] = useState<ResourceLimitPolicy | null>(null)
  const [loadError, setLoadError] = useState('')
  const [previewUsers, setPreviewUsers] = useState(10)

  useEffect(() => {
    if (!open || catalog) return
    getResourceLimits()
      .then((d) => { setCatalog(d.catalog); setPolicy(d.policy) })
      .catch((e) => setLoadError(errText(e, 'Could not load the resource-limit catalog')))
  }, [open, catalog])

  const setField = (key: string, field: keyof PlanResourceLimit, raw: string) => {
    const t = raw.trim()
    const n = t === '' ? null : Number(t)
    const entry = { ...(value[key] || {}), [field]: n !== null && Number.isFinite(n) ? n : null }
    onChange({ ...value, [key]: entry })
  }

  const numIn = (key: string, field: keyof PlanResourceLimit, placeholder: string) => (
    <input
      type="number"
      min={0}
      value={value[key]?.[field] ?? ''}
      placeholder={placeholder}
      onChange={(e) => setField(key, field, e.target.value)}
      style={{ width: '100%' }}
    />
  )

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none',
          padding: 0, cursor: 'pointer', color: 'var(--color-text)', fontSize: '0.9375rem', fontWeight: 600,
        }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />} Resource limits
      </button>
      {open && (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {loadError && <div style={{ color: 'var(--color-error)', fontSize: '0.875rem' }}>{loadError}</div>}
          {!catalog && !loadError && (
            <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>Loading…</div>
          )}
          {catalog && (
            <>
              <InfoBox>
                What one application on this plan may use of the shared server. Each value is
                Base + Per user × users, capped at Max and never above the instance ceiling.
                Blank = the built-in default.
              </InfoBox>
              <div style={{ maxWidth: 220 }}>
                <Field label="Preview for N users">
                  <input type="number" min={0} value={previewUsers}
                         onChange={(e) => setPreviewUsers(Math.max(0, parseInt(e.target.value, 10) || 0))}
                         style={{ width: '100%' }} />
                </Field>
              </div>
              {catalog.groups.map((g) => {
                const keys = Object.keys(catalog.limits).filter((k) =>
                  catalog.limits[k].group === g.id && catalog.limits[k].scope !== 'global')
                if (!keys.length) return null
                return (
                  <div key={g.id}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{g.label}</div>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <Th>Limit</Th>
                          <Th>Base</Th>
                          <Th>Per user</Th>
                          <Th>Max</Th>
                          <Th right>For {previewUsers} users</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {keys.map((k) => {
                          const d = catalog.limits[k]
                          const ceiling = policy?.ceilings?.[k] ?? d.ceiling
                          return (
                            <tr key={k}>
                              <Td>
                                {d.label}
                                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                                  {d.unit} · ceiling {ceiling.toLocaleString()}
                                </div>
                              </Td>
                              <Td style={{ width: 100 }}>{numIn(k, 'base', String(d.default))}</Td>
                              <Td style={{ width: 100 }}>
                                {d.scales ? numIn(k, 'per_user', '0') : <span style={{ color: 'var(--color-text-faint)' }}>—</span>}
                              </Td>
                              <Td style={{ width: 100 }}>{numIn(k, 'max', 'None')}</Td>
                              <Td right style={{ whiteSpace: 'nowrap' }}>
                                {effectiveLimit(value[k], d, ceiling, previewUsers).toLocaleString()} {d.unit}
                              </Td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              })}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/** Prices per (period, currency, country). A country row overrides its currency's default. */
/** The price row being typed — held by the editor so Save plan can save it too. */
export interface PriceDraft {
  period: string
  currency: string
  country: string
  amount: string
  providerPlanId: string
}

export const EMPTY_PRICE_DRAFT: PriceDraft = {
  period: 'monthly', currency: 'USD', country: '', amount: '', providerPlanId: '',
}

/** The upsert body for a draft row, or null when no amount was typed. */
export function draftToPrice(d: PriceDraft) {
  if (!d.amount.trim()) return null
  return {
    billing_period: d.period, currency: d.currency, country: d.country || null,
    amount_minor: majorToMinor(d.amount), provider_plan_id: d.providerPlanId.trim() || null,
  }
}

function PriceRepeater({ prices, draft, onDraft, onAdd, onRemove }: {
  prices: AdminPlan['prices']
  draft: PriceDraft
  onDraft: (d: PriceDraft) => void
  onAdd: () => void | Promise<void>
  onRemove: (priceId: number) => void | Promise<void>
}) {
  const setD = (patch: Partial<PriceDraft>) => onDraft({ ...draft, ...patch })

  return (
    <div>
      <div style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.5rem' }}>Pricing</div>
      <table style={{ ...tableStyle, marginTop: 0 }}>
        <thead>
          <tr>
            <Th>Period</Th><Th>Currency</Th><Th>Country</Th>
            <Th right>Amount</Th><Th>Payment gateway ID</Th><Th />
          </tr>
        </thead>
        <tbody>
          {prices.map((p) => (
            <tr key={p.id}>
              <Td>{p.billing_period}</Td>
              <Td>{p.currency}</Td>
              <Td>{p.country || <span style={{ color: 'var(--color-text-secondary)' }}>default</span>}</Td>
              <Td right>{minorToMajor(p.amount_minor)}</Td>
              <Td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                {p.provider_plan_id || '—'}
              </Td>
              <Td right>
                <button className="btn btn-secondary" style={{ padding: '0.1rem 0.35rem' }}
                        onClick={() => onRemove(p.id)}>
                  <Trash2 size={12} />
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: '1fr 1fr 1.4fr 1fr 1.6fr auto', alignItems: 'end', marginTop: '0.625rem' }}>
        <select value={draft.period} onChange={(e) => setD({ period: e.target.value })}>
          <option value="monthly">monthly</option>
          <option value="yearly">yearly</option>
        </select>
        <select value={draft.currency} onChange={(e) => setD({ currency: e.target.value })}>
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={draft.country} onChange={(e) => setD({ country: e.target.value })}>
          <option value="">Default for currency</option>
          {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        <input type="number" min={0} step="0.01" placeholder="Amount" value={draft.amount}
               onChange={(e) => setD({ amount: e.target.value })} />
        <input placeholder="Payment gateway ID (recurring)" value={draft.providerPlanId}
               onChange={(e) => setD({ providerPlanId: e.target.value })} />
        <button className="btn btn-secondary" onClick={() => void onAdd()}
                style={{ whiteSpace: 'nowrap' }}>
          <Plus size={14} /> Add price
        </button>
      </div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.375rem' }}>
        Save plan also saves a price typed here. A row with a country overrides the
        currency's default row. The payment gateway ID is the provider's own
        recurring-plan ID — without it the plan is charged as a one-time payment
        per period rather than auto-renewing.
      </div>
    </div>
  )
}
